import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

function TeacherAttendanceView({ teacherId, teacherName, userRole, onClose }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    if (teacherId) {
      fetchAttendanceData();
    }
  }, [teacherId, selectedMonth, selectedYear]);

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/teacher-attendance/${teacherId}`, {
        params: { month: selectedMonth, year: selectedYear }
      });
      setAttendanceData(response.data || []);
    } catch (error) {
      console.error('Error fetching teacher attendance:', error);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceStats = () => {
    const presentDays = attendanceData.filter(record => record.status === 'present').length;
    const absentDays = attendanceData.filter(record => record.status === 'absent').length;
    const lateDays = attendanceData.filter(record => record.status === 'late').length;
    const totalMarked = presentDays + absentDays + lateDays;
    const percentage = totalMarked > 0 ? ((presentDays + lateDays) / totalMarked * 100).toFixed(1) : 0;

    return {
      presentDays,
      absentDays,
      lateDays,
      totalMarked,
      percentage
    };
  };

  const stats = getAttendanceStats();

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100%', 
      height: '100%', 
      background: 'rgba(0,0,0,0.8)', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{ 
        background: 'white', 
        borderRadius: '15px', 
        width: '100%', 
        maxWidth: '800px', 
        maxHeight: '90vh', 
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ 
          padding: '20px', 
          borderBottom: '2px solid #2196f3', 
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          borderRadius: '15px 15px 0 0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, color: '#1565c0', fontSize: '24px', fontWeight: 'bold' }}>
                📊 Teacher Attendance - {teacherName}
              </h2>
              <p style={{ margin: '5px 0 0 0', color: '#1565c0', fontSize: '16px' }}>
                Monthly Attendance Summary
              </p>
            </div>
            <button 
              onClick={onClose}
              style={{ 
                background: '#dc2626', 
                color: 'white', 
                border: 'none', 
                padding: '10px 15px', 
                borderRadius: '50%', 
                cursor: 'pointer',
                fontSize: '18px',
                width: '45px',
                height: '45px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {/* Month/Year Selector */}
          <div style={{ 
            display: 'flex', 
            gap: '15px', 
            alignItems: 'center', 
            marginBottom: '25px',
            justifyContent: 'center'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                Month:
              </label>
              <select 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                style={{ 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  border: '2px solid #2196f3', 
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                Year:
              </label>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  border: '2px solid #2196f3', 
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {[2023, 2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div style={{ fontSize: '18px' }}>Loading attendance data...</div>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '15px', 
                marginBottom: '25px' 
              }}>
                <div style={{ 
                  background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)', 
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.presentDays}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Present Days</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #f44336 0%, #e53935 100%)', 
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.absentDays}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Absent Days</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', 
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.lateDays}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Late Days</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', 
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.percentage}%
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Attendance %</div>
                </div>
              </div>

              {/* Detailed Records */}
              <div style={{ 
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '20px',
                border: '1px solid #dee2e6'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '18px' }}>
                  📅 Daily Records - {monthNames[selectedMonth - 1]} {selectedYear}
                </h4>
                
                {attendanceData.length > 0 ? (
                  <div style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto', 
                    border: '1px solid #ddd', 
                    borderRadius: '8px',
                    background: 'white'
                  }}>
                    {attendanceData.slice().reverse().map(record => (
                      <div key={record._id} style={{ 
                        padding: '12px', 
                        borderBottom: '1px solid #eee', 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <strong style={{ fontSize: '16px' }}>
                            {new Date(record.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </strong>
                          {record.remarks && (
                            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                              Remarks: {record.remarks}
                            </p>
                          )}
                          <small style={{ color: '#888', fontSize: '12px' }}>
                            Marked by: {record.markedBy}
                          </small>
                        </div>
                        <span style={{ 
                          padding: '6px 12px', 
                          borderRadius: '20px', 
                          fontSize: '12px', 
                          fontWeight: 'bold',
                          background: record.status === 'present' ? '#4caf50' : 
                                     record.status === 'late' ? '#ff9800' : '#f44336',
                          color: 'white'
                        }}>
                          {record.status.toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px', 
                    color: '#666',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '15px' }}>📅</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                      No Attendance Records
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      No attendance has been marked for {monthNames[selectedMonth - 1]} {selectedYear}
                    </div>
                  </div>
                )}
              </div>

              {/* Performance Summary */}
              {stats.totalMarked > 0 && (
                <div style={{ 
                  background: stats.percentage >= 90 ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)' :
                             stats.percentage >= 75 ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' :
                             'linear-gradient(135deg, #f44336 0%, #e53935 100%)',
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>
                    📊 Performance Summary
                  </h4>
                  <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
                    <div>Total Days Marked: {stats.totalMarked}</div>
                    <div>Attendance Rate: {stats.percentage}%</div>
                    <div style={{ marginTop: '10px', fontSize: '14px', opacity: 0.9 }}>
                      {stats.percentage >= 90 ? '🌟 Excellent Attendance!' :
                       stats.percentage >= 75 ? '👍 Good Attendance' :
                       '⚠️ Needs Improvement'}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default TeacherAttendanceView;